/**
 * Weather on the map (docs/specs/map.md §15; Diurnal inventory 05 F2 and F14; sign-off 05-006 to 05-010; MB
 * decision 94: keep the Diurnal tool's weather services for Thomson).
 *
 * Open-Meteo (wind, gust and temperature layers, the map-click weather, the wind, sky and rain panels) and RainViewer
 * (radar) need no key and are called by Teslyn Field itself (§15.1). This module has the requests, the reading of
 * their answers and every rule the Diurnal tool applied (`aircraft-map.js`, cited as am:<line>): grid, colour scales,
 * legends, thresholds, words, caches, the mode bar and the radar frames. No network and no DOM here.
 */
import type { GeoBounds } from './map-geo.js';
import type { OverlayId } from './tile-cache.js';

// ---------------------------------------------------------------------------------------------- services

/** Open-Meteo's free host (non-commercial use only; a paid plan uses `customer-api.open-meteo.com` with a key). */
export const OPEN_METEO_FREE_HOST = 'https://api.open-meteo.com';
export const RAINVIEWER_MAPS_URL = 'https://api.rainviewer.com/public/weather-maps.json';
export const RAINVIEWER_TILE_HOST = 'https://tilecache.rainviewer.com';
/** Radar coverage: opaque inside radar reach, clear outside (am:31273–31276). */
export const RAINVIEWER_COVERAGE_URL = `${RAINVIEWER_TILE_HOST}/v2/coverage/0/256/{z}/{x}/{y}/0/0_0.png`;

export const OPEN_METEO_CREDIT = 'Open-Meteo';
export const RAINVIEWER_CREDIT = '© RainViewer, personal and educational use';

// ---------------------------------------------------------------------------------------------- layers and modes

/** The colour-mapped weather layers Teslyn draws itself from the Open-Meteo grid (am:29520–29523). */
export type WeatherCanvasId = 'wind' | 'gusts' | 'temperature';
export type GridField = 'wind_speed_10m' | 'wind_gusts_10m' | 'temperature_2m';
/** The weather panels (am:30597, 30723, 31068, 31209). */
export type WeatherWidgetId = 'wind' | 'sky' | 'rain' | 'radar';
/** The mode bar's buttons, in its order (am:29224–29231). */
export type WeatherModeId = 'wind' | 'gusts' | 'cloud' | 'rain' | 'radar' | 'temperature';

export interface WeatherModeDef {
  readonly id: WeatherModeId;
  readonly label: string;
  /** The panel the button opens with it. */
  readonly widget: WeatherWidgetId;
  /** The colour-mapped layer it draws. */
  readonly canvas?: WeatherCanvasId;
  /** The OpenWeatherMap tile overlay it switches (drawn once the tenant's key reaches Field, 05-004). */
  readonly overlay?: OverlayId;
}

export const WEATHER_MODES: readonly WeatherModeDef[] = [
  { id: 'wind', label: 'Wind', widget: 'wind', canvas: 'wind' },
  { id: 'gusts', label: 'Gusts', widget: 'wind', canvas: 'gusts' },
  { id: 'cloud', label: 'Cloud', widget: 'sky', overlay: 'weather_clouds' },
  { id: 'rain', label: 'Rain', widget: 'rain', overlay: 'weather_precipitation' },
  { id: 'radar', label: 'Radar', widget: 'radar' },
  { id: 'temperature', label: 'Temp', widget: 'sky', canvas: 'temperature' },
];

export const WEATHER_WIDGETS: ReadonlyArray<readonly [WeatherWidgetId, string]> = [
  ['wind', 'Wind'],
  ['sky', 'Sky conditions'],
  ['rain', 'Precipitation'],
  ['radar', 'Radar'],
];

export const CANVAS_FIELD: Readonly<Record<WeatherCanvasId, GridField>> = {
  wind: 'wind_speed_10m',
  gusts: 'wind_gusts_10m',
  temperature: 'temperature_2m',
};

/** Canvas layers start at 40 % so the map stays readable (am:29525–29527). */
export const WEATHER_DEFAULT_OPACITY = 0.4;

export interface ScaleStop {
  readonly v: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  /** 0–255. */
  readonly a: number;
}

/** The Diurnal tool's colour scales, value for value (am:29496–29521): data colour maps, never re-tinted. */
export const WEATHER_SCALES: Readonly<Record<GridField, readonly ScaleStop[]>> = {
  wind_speed_10m: [
    { v: 0, r: 0, g: 0, b: 0, a: 0 },
    { v: 5, r: 40, g: 210, b: 60, a: 150 },
    { v: 15, r: 220, g: 200, b: 0, a: 175 },
    { v: 30, r: 220, g: 110, b: 0, a: 200 },
    { v: 45, r: 220, g: 30, b: 30, a: 220 },
    { v: 55, r: 120, g: 0, b: 0, a: 240 },
  ],
  wind_gusts_10m: [
    { v: 0, r: 0, g: 0, b: 0, a: 0 },
    { v: 8, r: 40, g: 180, b: 210, a: 150 },
    { v: 20, r: 40, g: 90, b: 220, a: 175 },
    { v: 35, r: 110, g: 40, b: 220, a: 200 },
    { v: 50, r: 210, g: 40, b: 170, a: 220 },
    { v: 65, r: 120, g: 0, b: 80, a: 240 },
  ],
  temperature_2m: [
    { v: -20, r: 20, g: 0, b: 180, a: 190 },
    { v: 0, r: 0, g: 175, b: 220, a: 170 },
    { v: 15, r: 0, g: 200, b: 70, a: 155 },
    { v: 25, r: 215, g: 200, b: 0, a: 175 },
    { v: 35, r: 220, g: 95, b: 0, a: 200 },
    { v: 45, r: 215, g: 25, b: 25, a: 220 },
    { v: 55, r: 120, g: 0, b: 0, a: 235 },
  ],
};

export type LegendId = WeatherCanvasId | 'cloud' | 'rain';

export interface WeatherLegend {
  readonly id: LegendId;
  readonly label: string;
  /** CSS colours left to right. */
  readonly stops: readonly string[];
  readonly ticks: readonly string[];
}

const css = (s: ScaleStop): string =>
  s.a === 0 ? `rgba(${s.r},${s.g},${s.b},0)` : `rgb(${s.r},${s.g},${s.b})`;

function scaleLegend(id: WeatherCanvasId, label: string): WeatherLegend {
  const scale = WEATHER_SCALES[CANVAS_FIELD[id]];
  return {
    id,
    label,
    stops: scale.map(css),
    ticks: scale.map((s, i) => `${s.v}${i === scale.length - 1 ? '+' : ''}`),
  };
}

/**
 * Legend cards in mode bar order (am:29295–29324). The gradients of the canvas layers come from their scales (the
 * Diurnal cards wrote the same colours by hand); cloud and precipitation are OpenWeatherMap's own tile scales.
 */
export const WEATHER_LEGENDS: readonly WeatherLegend[] = [
  scaleLegend('wind', 'Wind speed (kt)'),
  scaleLegend('gusts', 'Wind gusts (kt)'),
  {
    id: 'cloud',
    label: 'Cloud cover (%)',
    stops: ['rgba(255,255,255,0)', 'rgb(246,245,255)', 'rgb(233,233,223)', 'rgb(210,210,210)'],
    ticks: ['0', '30', '60', '100'],
  },
  {
    id: 'rain',
    label: 'Precipitation (OpenWeatherMap)',
    stops: [
      'rgb(254,249,202)',
      'rgb(147,245,125)',
      'rgb(80,176,51)',
      'rgb(32,78,17)',
      'rgb(242,163,58)',
      'rgb(235,71,38)',
      'rgb(9,10,8)',
    ],
    ticks: ['0', 'trace', 'light', 'moderate', 'heavy', 'intense', 'extreme'],
  },
  scaleLegend('temperature', 'Temperature (°C)'),
];

/** The colour of a value on a scale: below the first stop the first colour, above the last the last (am:29691). */
export function colourAt(value: number, scale: readonly ScaleStop[]): ScaleStop {
  const first = scale[0]!;
  const last = scale[scale.length - 1]!;
  if (!Number.isFinite(value)) return { v: value, r: 0, g: 0, b: 0, a: 0 };
  if (value <= first.v) return { ...first };
  if (value >= last.v) return { ...last };
  for (let i = 1; i < scale.length; i += 1) {
    const hi = scale[i]!;
    if (value <= hi.v) {
      const lo = scale[i - 1]!;
      const t = (value - lo.v) / (hi.v - lo.v);
      const lerp = (a: number, b: number): number => Math.round(a + t * (b - a));
      return { v: value, r: lerp(lo.r, hi.r), g: lerp(lo.g, hi.g), b: lerp(lo.b, hi.b), a: lerp(lo.a, hi.a) };
    }
  }
  return { ...last };
}

// ---------------------------------------------------------------------------------------------- the grid

export const GRID_SIZE = 15;
/** The grid covers the view plus 15 % of its span on each side (am:29606–29620). */
export const GRID_EXTEND = 0.15;
export const GRID_CACHE_MS = 30 * 60 * 1000;
/** Redraw waits this long after the map stops (am:29585). */
export const GRID_RENDER_DEBOUNCE_MS = 350;
export const GRID_FIELDS: readonly GridField[] = ['wind_speed_10m', 'wind_gusts_10m', 'temperature_2m'];

export interface WeatherGridRequest {
  /** The box the grid covers. */
  readonly bounds: GeoBounds;
  readonly n: number;
  /** `n × n` points, rows from the south, each row from the west; rounded to 3 decimals as sent. */
  readonly lats: readonly number[];
  readonly lons: readonly number[];
}

const round3 = (v: number): number => Math.round(v * 1000) / 1000;

/** The grid for a map view, or null for a view with no area. */
export function weatherGridRequest(
  view: GeoBounds,
  n = GRID_SIZE,
  extend = GRID_EXTEND,
): WeatherGridRequest | null {
  const latSpan = view.north - view.south;
  const lonSpan = view.east - view.west;
  const south = Math.max(-85, view.south - latSpan * extend);
  const north = Math.min(85, view.north + latSpan * extend);
  const west = Math.max(-180, view.west - lonSpan * extend);
  const east = Math.min(180, view.east + lonSpan * extend);
  if (!(south < north) || !(west < east) || n < 2) return null;
  const lats: number[] = [];
  const lons: number[] = [];
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      lats.push(round3(south + (north - south) * (i / (n - 1))));
      lons.push(round3(west + (east - west) * (j / (n - 1))));
    }
  }
  return { bounds: { south, west, north, east }, n, lats, lons };
}

const list = (v: readonly number[]): string => v.map((x) => x.toFixed(3)).join(',');

/** The grid request (am:29632), on the free host unless another is given. */
export function openMeteoGridUrl(req: WeatherGridRequest, host = OPEN_METEO_FREE_HOST): string {
  return (
    `${host}/v1/forecast?latitude=${list(req.lats)}&longitude=${list(req.lons)}` +
    `&current=${GRID_FIELDS.join(',')}&wind_speed_unit=kn&timezone=auto`
  );
}

export interface WeatherGrid {
  readonly bounds: GeoBounds;
  readonly n: number;
  /** Values in request order; NaN where Open-Meteo gave none (drawn clear). Wind and gusts in kt, temperature °C. */
  readonly fields: Readonly<Record<GridField, readonly number[]>>;
  /** The model time of the answer (local, ISO without offset), and its offset from UTC. */
  readonly time: string | null;
  readonly utcOffsetSeconds: number | null;
  /** When it was fetched (ms since 1970). */
  readonly fetchedAt: number;
}

export type Read<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: string };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Open-Meteo's own reason for a refusal, in Teslyn's words. */
function refusal(raw: unknown): string {
  const reason = isObj(raw) && typeof raw.reason === 'string' ? raw.reason.slice(0, 200) : null;
  return reason
    ? `Open-Meteo refused the request: ${reason}`
    : 'Open-Meteo sent an answer Teslyn cannot read.';
}

/** Read the grid answer: an array of one answer per point, in request order (am:29633–29646). */
export function parseOpenMeteoGrid(
  raw: unknown,
  req: WeatherGridRequest,
  fetchedAt: number,
): Read<WeatherGrid> {
  if (!Array.isArray(raw)) return { ok: false, error: refusal(raw) };
  if (raw.length !== req.n * req.n) {
    return { ok: false, error: `Open-Meteo answered ${raw.length} points of ${req.n * req.n}.` };
  }
  const fields = Object.fromEntries(GRID_FIELDS.map((f) => [f, [] as number[]])) as Record<
    GridField,
    number[]
  >;
  let time: string | null = null;
  let offset: number | null = null;
  for (const r of raw) {
    const cur = isObj(r) && isObj(r.current) ? r.current : {};
    for (const f of GRID_FIELDS) fields[f].push(num(cur[f]) ?? Number.NaN);
    if (time === null && typeof cur.time === 'string') time = cur.time;
    if (offset === null && isObj(r)) offset = num(r.utc_offset_seconds);
  }
  return {
    ok: true,
    value: { bounds: req.bounds, n: req.n, fields, time, utcOffsetSeconds: offset, fetchedAt },
  };
}

const shrink = (b: GeoBounds, f: number): GeoBounds => {
  const dLat = (b.north - b.south) * f;
  const dLon = (b.east - b.west) * f;
  return { south: b.south + dLat, north: b.north - dLat, west: b.west + dLon, east: b.east - dLon };
};

const contains = (outer: GeoBounds, inner: GeoBounds): boolean =>
  inner.south >= outer.south &&
  inner.north <= outer.north &&
  inner.west >= outer.west &&
  inner.east <= outer.east;

/** A new grid is needed: none yet, older than 30 minutes, or the view (shrunk by 5 %) is no longer inside it. */
export function gridIsStale(grid: WeatherGrid | null, view: GeoBounds, now: number): boolean {
  if (!grid) return true;
  if (now - grid.fetchedAt > GRID_CACHE_MS) return true;
  return !contains(grid.bounds, shrink(view, 0.05));
}

/** The bilinear value of a grid field at a place, NaN outside the grid (am:29660–29676). */
export function gridValueAt(grid: WeatherGrid, field: GridField, lat: number, lon: number): number {
  const { n, bounds } = grid;
  const gi = ((lat - bounds.south) / (bounds.north - bounds.south)) * (n - 1);
  const gj = ((lon - bounds.west) / (bounds.east - bounds.west)) * (n - 1);
  if (!(gi >= 0 && gi <= n - 1 && gj >= 0 && gj <= n - 1)) return Number.NaN;
  const v = grid.fields[field];
  const i0 = Math.floor(gi);
  const j0 = Math.floor(gj);
  const i1 = Math.min(i0 + 1, n - 1);
  const j1 = Math.min(j0 + 1, n - 1);
  const ti = gi - i0;
  const tj = gj - j0;
  const at = (i: number, j: number): number => v[i * n + j] ?? Number.NaN;
  return (
    (1 - ti) * (1 - tj) * at(i0, j0) +
    ti * (1 - tj) * at(i1, j0) +
    (1 - ti) * tj * at(i0, j1) +
    ti * tj * at(i1, j1)
  );
}

/** The image is 64 × 64 pixels, scaled smoothly to the map (am:29650). */
export const WEATHER_IMAGE_SIZE = 64;

/**
 * RGBA pixels of a layer for the map: one row per latitude in `rowLats` (top first, from the map's projection),
 * one column per longitude in `colLons`. Outside the grid, or where a value is missing, the pixel is clear.
 */
export function weatherImage(
  grid: WeatherGrid,
  field: GridField,
  rowLats: readonly number[],
  colLons: readonly number[],
): Uint8ClampedArray {
  const scale = WEATHER_SCALES[field];
  const out = new Uint8ClampedArray(rowLats.length * colLons.length * 4);
  rowLats.forEach((lat, row) => {
    colLons.forEach((lon, col) => {
      const c = colourAt(gridValueAt(grid, field, lat, lon), scale);
      const k = (row * colLons.length + col) * 4;
      out[k] = c.r;
      out[k + 1] = c.g;
      out[k + 2] = c.b;
      out[k + 3] = c.a;
    });
  });
  return out;
}

// ---------------------------------------------------------------------------------------------- the point weather

export const POINT_VARIABLES = [
  'wind_speed_10m',
  'wind_direction_10m',
  'wind_gusts_10m',
  'temperature_2m',
  'relative_humidity_2m',
  'dew_point_2m',
  'cloud_cover',
  'cloud_cover_low',
  'cloud_cover_mid',
  'cloud_cover_high',
  'visibility',
  'cape',
  'freezing_level_height',
  'pressure_msl',
  'precipitation',
  'weather_code',
] as const;

/** The point request (am:30455) with the model dew point added (§15.4). */
export function openMeteoPointUrl(lat: number, lon: number, host = OPEN_METEO_FREE_HOST): string {
  return (
    `${host}/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}` +
    `&current=${POINT_VARIABLES.join(',')}&wind_speed_unit=ms&timezone=auto`
  );
}

export interface PointWeather {
  readonly lat: number;
  readonly lon: number;
  /** Model time, local (ISO without offset), its offset from UTC and the interval of the "current" values. */
  readonly time: string | null;
  readonly utcOffsetSeconds: number | null;
  readonly intervalSeconds: number | null;
  readonly windMs: number | null;
  readonly windDirDeg: number | null;
  readonly gustMs: number | null;
  readonly tempC: number | null;
  readonly humidity: number | null;
  /** The model's dew point, else the Diurnal estimate (`dewPointSource`). */
  readonly dewPointC: number | null;
  readonly dewPointSource: 'model' | 'estimate' | null;
  readonly cloud: number | null;
  readonly cloudLow: number | null;
  readonly cloudMid: number | null;
  readonly cloudHigh: number | null;
  readonly visibilityM: number | null;
  readonly cape: number | null;
  readonly freezingLevelM: number | null;
  readonly pressureHpa: number | null;
  /** Precipitation over the interval, mm. */
  readonly precipitationMm: number | null;
  readonly weatherCode: number | null;
  readonly fetchedAt: number;
}

/** The Diurnal dew point estimate (am:30467), used only when the answer has no dew point. */
export const estimateDewPoint = (tempC: number, humidity: number): number => tempC - (100 - humidity) / 5;

/** Read one point answer (am:30458–30489). Missing values stay missing; nothing is assumed to be 0. */
export function parseOpenMeteoPoint(raw: unknown, fetchedAt: number): Read<PointWeather> {
  if (!isObj(raw) || raw.error === true || !isObj(raw.current)) return { ok: false, error: refusal(raw) };
  const c = raw.current;
  const tempC = num(c.temperature_2m);
  const humidity = num(c.relative_humidity_2m);
  const modelDew = num(c.dew_point_2m);
  const dew = modelDew ?? (tempC !== null && humidity !== null ? estimateDewPoint(tempC, humidity) : null);
  return {
    ok: true,
    value: {
      lat: num(raw.latitude) ?? Number.NaN,
      lon: num(raw.longitude) ?? Number.NaN,
      time: typeof c.time === 'string' ? c.time : null,
      utcOffsetSeconds: num(raw.utc_offset_seconds),
      intervalSeconds: num(c.interval),
      windMs: num(c.wind_speed_10m),
      windDirDeg: num(c.wind_direction_10m),
      gustMs: num(c.wind_gusts_10m),
      tempC,
      humidity,
      dewPointC: dew,
      dewPointSource: modelDew !== null ? 'model' : dew !== null ? 'estimate' : null,
      cloud: num(c.cloud_cover),
      cloudLow: num(c.cloud_cover_low),
      cloudMid: num(c.cloud_cover_mid),
      cloudHigh: num(c.cloud_cover_high),
      visibilityM: num(c.visibility),
      cape: num(c.cape),
      freezingLevelM: num(c.freezing_level_height),
      pressureHpa: num(c.pressure_msl),
      precipitationMm: num(c.precipitation),
      weatherCode: num(c.weather_code),
      fetchedAt,
    },
  };
}

/** Point answers are reused for 4 minutes within 0.05° (am:30449–30453). */
export const POINT_CACHE_MS = 4 * 60 * 1000;
export const POINT_CACHE_DEGREES = 0.05;

export function pointCacheHit(
  cached: PointWeather | null,
  at: { lat: number; lon: number },
  now: number,
): boolean {
  if (!cached) return false;
  return (
    now - cached.fetchedAt < POINT_CACHE_MS &&
    Math.abs(at.lat - cached.lat) < POINT_CACHE_DEGREES &&
    Math.abs(at.lon - cached.lon) < POINT_CACHE_DEGREES
  );
}

/** The key that shares one request already under way for the same place (0.05° cells). */
export const pointRequestKey = (lat: number, lon: number): string =>
  `${Math.round(lat / POINT_CACHE_DEGREES)}:${Math.round(lon / POINT_CACHE_DEGREES)}`;

// ---------------------------------------------------------------------------------------------- conversions

export const knots = (ms: number): number => (ms * 3600) / 1852;
export const kmh = (ms: number): number => ms * 3.6;

const COMPASS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
];

/** 16-point compass (am:30651–30654). */
export function compass16(deg: number): string {
  const i = Math.round((((deg % 360) + 360) % 360) / 22.5) % 16;
  return COMPASS[i]!;
}

/** Estimated cloud base: 400 ft per °C of spread, rounded to 100 ft (am:30468). */
export const cloudBaseFt = (tempC: number, dewPointC: number): number =>
  Math.round(((tempC - dewPointC) * 400) / 100) * 100;

/** Metres to feet, rounded to 100 ft (am:30486). */
export const feet100 = (m: number): number => Math.round((m * 3.28084) / 100) * 100;

/**
 * The rain rate, mm/h: the current precipitation is the sum over the answer's interval (900 s), not an hour; the
 * Diurnal tool showed the 15-minute amount as mm/h (§15.4). An answer without an interval is taken as hourly.
 */
export function precipRateMmH(w: Pick<PointWeather, 'precipitationMm' | 'intervalSeconds'>): number | null {
  if (w.precipitationMm === null) return null;
  const interval = w.intervalSeconds && w.intervalSeconds > 0 ? w.intervalSeconds : 3600;
  return (w.precipitationMm * 3600) / interval;
}

/** WMO weather codes in words (am:30462). */
export const WMO_TEXT: Readonly<Record<number, string>> = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Icy fog',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Heavy drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  66: 'Freezing rain',
  67: 'Heavy freezing rain',
  71: 'Slight snow',
  73: 'Moderate snow',
  75: 'Heavy snow',
  77: 'Snow grains',
  80: 'Rain showers',
  81: 'Moderate showers',
  82: 'Violent showers',
  85: 'Slight snow showers',
  86: 'Heavy snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Thunderstorm, heavy hail',
};

export const weatherText = (code: number | null): string | null =>
  code === null ? null : (WMO_TEXT[code] ?? `Weather code ${code}`);

// ---------------------------------------------------------------------------------------------- thresholds

/** `caution` is drawn in the `warn` colour, `warning` in the `fail` colour, always with the word (§15.4). */
export type WeatherLevel = 'ok' | 'caution' | 'warning';

/** Storm risk (CAPE, J/kg): caution from 500, warning from 2500 (am:29429). */
export const capeLevel = (cape: number): WeatherLevel =>
  cape >= 2500 ? 'warning' : cape >= 500 ? 'caution' : 'ok';

/** Storm risk in words (am:30464–30465). */
export const capeLabel = (cape: number): string =>
  cape < 500
    ? 'Low storm risk'
    : cape < 1500
      ? 'Moderate storm risk'
      : cape < 2500
        ? 'High storm risk'
        : 'Extreme storm risk';

/** Low cloud %: caution over 20, warning over 50 (am:29430). */
export const lowCloudLevel = (pct: number): WeatherLevel =>
  pct > 50 ? 'warning' : pct > 20 ? 'caution' : 'ok';

/** Rain mm/h: caution from 2.5, warning from 7.5 (am:29446). */
export const rainLevel = (mmh: number): WeatherLevel =>
  mmh >= 7.5 ? 'warning' : mmh >= 2.5 ? 'caution' : 'ok';

/** Temperature °C: warning from 38 (am:29458). */
export const temperatureLevel = (c: number): WeatherLevel => (c >= 38 ? 'warning' : 'ok');

/** Estimated cloud base ft: caution under 3000, warning under 1000 (am:30893). */
export const cloudBaseLevel = (ft: number): WeatherLevel =>
  ft < 1000 ? 'warning' : ft < 3000 ? 'caution' : 'ok';

/** Visibility km: caution under 8, warning under 3 (am:30901). */
export const visibilityLevel = (km: number): WeatherLevel => (km < 3 ? 'warning' : km < 8 ? 'caution' : 'ok');

export type WindClass = 'Calm' | 'Moderate' | 'Strong' | 'Extreme';

/** Wind m/s: calm under 5.5, moderate under 11.1, strong under 16.7, else extreme (am:30706–30709). */
export function windClass(ms: number): { readonly word: WindClass; readonly level: WeatherLevel } {
  if (ms < 5.5) return { word: 'Calm', level: 'ok' };
  if (ms < 11.1) return { word: 'Moderate', level: 'ok' };
  if (ms < 16.7) return { word: 'Strong', level: 'caution' };
  return { word: 'Extreme', level: 'warning' };
}

export type RainClass = 'None' | 'Light' | 'Moderate' | 'Heavy';

/** Rain mm/h: light under 2.5, moderate under 7.5, else heavy (am:31100–31103). */
export function rainClass(mmh: number): { readonly word: RainClass; readonly level: WeatherLevel } {
  if (mmh <= 0) return { word: 'None', level: 'ok' };
  if (mmh < 2.5) return { word: 'Light', level: 'ok' };
  if (mmh < 7.5) return { word: 'Moderate', level: 'caution' };
  return { word: 'Heavy', level: 'warning' };
}

// ---------------------------------------------------------------------------------------------- words

const AU = 'en-AU';
const fmt = (v: number, digits = 0): string =>
  v.toLocaleString(AU, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const visibilityText = (m: number): string =>
  m >= 10_000 ? 'more than 10 km' : `${fmt(m / 1000, 1)} km`;

export interface WeatherRow {
  readonly label: string;
  readonly value: string;
  readonly level?: WeatherLevel;
}

export interface WeatherSection {
  readonly title: string;
  readonly rows: readonly WeatherRow[];
}

const row = (label: string, value: string, level?: WeatherLevel): WeatherRow =>
  level && level !== 'ok' ? { label, value, level } : { label, value };

/** The cloud base when low cloud is over 5 % (am:29437), with its source. */
function baseRow(w: PointWeather, label: string, withLevel: boolean): WeatherRow | null {
  if (w.cloudLow === null || w.cloudLow <= 5 || w.tempC === null || w.dewPointC === null) return null;
  const ft = cloudBaseFt(w.tempC, w.dewPointC);
  const est = w.dewPointSource === 'estimate' ? ' (dew point estimated)' : '';
  return row(label, `about ${fmt(ft)} ft${est}`, withLevel ? cloudBaseLevel(ft) : undefined);
}

export interface PopupShows {
  readonly wind: boolean;
  readonly rain: boolean;
  readonly temperature: boolean;
  readonly cloud: boolean;
}

/** The map-click weather, section by section as the Diurnal popup (am:29420–29486). */
export function weatherPopup(w: PointWeather, show: PopupShows): WeatherSection[] {
  const out: WeatherSection[] = [];
  const rows = (...r: Array<WeatherRow | null>): WeatherRow[] => r.filter((x): x is WeatherRow => x !== null);
  const freezing = w.freezingLevelM !== null ? `${fmt(feet100(w.freezingLevelM))} ft` : null;
  if (show.wind) {
    const deg = w.windDirDeg;
    out.push({
      title: 'Wind',
      rows: rows(
        w.windMs !== null
          ? row(
              'Wind',
              `${deg !== null ? `${compass16(deg)} (${fmt(deg)}°) ` : ''}${fmt(knots(w.windMs))} kt`,
            )
          : row('Wind', 'not given'),
        w.gustMs !== null && w.gustMs > 0 ? row('Gusts', `${fmt(knots(w.gustMs))} kt`) : null,
        w.visibilityM !== null ? row('Visibility', visibilityText(w.visibilityM)) : null,
        w.cloudLow !== null ? row('Low cloud', `${fmt(w.cloudLow)} %`, lowCloudLevel(w.cloudLow)) : null,
        baseRow(w, 'Estimated cloud base', false),
        freezing ? row('Freezing level', freezing) : null,
        w.cape !== null ? row('Storm risk', capeLabel(w.cape), capeLevel(w.cape)) : null,
      ),
    });
  }
  if (show.rain) {
    const rate = precipRateMmH(w);
    const text = weatherText(w.weatherCode);
    out.push({
      title: 'Rain',
      rows: rows(
        rate === null
          ? row('Precipitation', 'not given')
          : row('Precipitation', rate > 0 ? `${fmt(rate, 1)} mm/h` : 'None', rainLevel(rate)),
        text ? row('Condition', text) : null,
        w.cloud !== null ? row('Cloud cover', `${fmt(w.cloud)} %`) : null,
        w.humidity !== null ? row('Humidity', `${fmt(w.humidity)} %`) : null,
      ),
    });
  }
  if (show.temperature) {
    out.push({
      title: 'Temperature',
      rows: rows(
        w.tempC !== null
          ? row('Temperature', `${fmt(w.tempC, 1)} °C`, temperatureLevel(w.tempC))
          : row('Temperature', 'not given'),
        w.dewPointC !== null
          ? row(
              'Dew point',
              `${fmt(w.dewPointC, 1)} °C${w.dewPointSource === 'estimate' ? ' (estimated)' : ''}`,
            )
          : null,
        w.humidity !== null ? row('Humidity', `${fmt(w.humidity)} %`) : null,
        freezing ? row('Freezing level', freezing) : null,
      ),
    });
  }
  if (show.cloud) {
    out.push({
      title: 'Cloud cover',
      rows: rows(
        w.cloud !== null ? row('Total cloud', `${fmt(w.cloud)} %`) : row('Total cloud', 'not given'),
        w.cloudLow !== null ? row('Low cloud', `${fmt(w.cloudLow)} %`, lowCloudLevel(w.cloudLow)) : null,
        baseRow(w, 'Estimated cloud base', false),
        w.visibilityM !== null ? row('Visibility', visibilityText(w.visibilityM)) : null,
      ),
    });
  }
  return out;
}

const pad2 = (v: number): string => String(v).padStart(2, '0');

/** "Open-Meteo forecast for 22:45 (UTC+8), fetched 2 min ago": every value shows its source and age (F14). */
export function weatherSourceText(
  w: Pick<PointWeather, 'time' | 'utcOffsetSeconds' | 'fetchedAt'>,
  now: number,
): string {
  const hhmm = w.time?.match(/T(\d\d:\d\d)/)?.[1];
  let zone = '';
  if (w.utcOffsetSeconds !== null) {
    const h = w.utcOffsetSeconds / 3600;
    zone = h === 0 ? ' (UTC)' : ` (UTC${h > 0 ? '+' : '−'}${fmt(Math.abs(h), Number.isInteger(h) ? 0 : 1)})`;
  }
  const mins = Math.max(0, Math.round((now - w.fetchedAt) / 60_000));
  const age = mins < 1 ? 'fetched just now' : `fetched ${mins} min ago`;
  return `${OPEN_METEO_CREDIT} forecast${hhmm ? ` for ${hhmm}${zone}` : ''}, ${age}`;
}

// ---------------------------------------------------------------------------------------------- panels

export interface WindPanel {
  readonly kmh: string;
  readonly kt: string;
  readonly gustKmh: string | null;
  readonly gustKt: string | null;
  readonly dirDeg: number | null;
  readonly dirText: string | null;
  readonly word: WindClass;
  readonly level: WeatherLevel;
  readonly detail: string;
  readonly pressure: string | null;
}

/** The wind panel (am:30656–30711), or null when the answer has no wind. */
export function windPanel(w: PointWeather): WindPanel | null {
  if (w.windMs === null) return null;
  const speed = `${fmt(kmh(w.windMs))} km/h (${fmt(knots(w.windMs))} kt)`;
  const gust = w.gustMs !== null && w.gustMs > 0 ? w.gustMs : null;
  const dir = w.windDirDeg !== null ? compass16(w.windDirDeg) : null;
  const text = weatherText(w.weatherCode);
  const c = windClass(w.windMs);
  return {
    kmh: fmt(kmh(w.windMs)),
    kt: fmt(knots(w.windMs)),
    gustKmh: gust !== null ? fmt(kmh(gust)) : null,
    gustKt: gust !== null ? fmt(knots(gust)) : null,
    dirDeg: w.windDirDeg,
    dirText: dir !== null ? `${dir} (${fmt(w.windDirDeg!)}°)` : null,
    word: c.word,
    level: c.level,
    detail:
      `${dir ? `${dir} ` : ''}${speed}` +
      (gust !== null ? ` gusts ${fmt(kmh(gust))} km/h (${fmt(knots(gust))} kt)` : '') +
      (text ? ` · ${text}` : ''),
    pressure: w.pressureHpa !== null ? `${fmt(w.pressureHpa, 1)} hPa` : null,
  };
}

export interface SkyPanel {
  readonly cloud: string;
  readonly rows: readonly WeatherRow[];
  readonly detail: string;
}

/** The sky conditions panel from Open-Meteo (am:30870–30932); the METAR part is 05-009. */
export function skyPanel(w: PointWeather): SkyPanel {
  const rows: WeatherRow[] = [];
  rows.push(
    w.cloudLow !== null
      ? row('Low cloud', `${fmt(w.cloudLow)} %`, lowCloudLevel(w.cloudLow))
      : row('Low cloud', 'not given'),
  );
  rows.push(row('Mid cloud', w.cloudMid !== null ? `${fmt(w.cloudMid)} %` : 'not given'));
  rows.push(baseRow(w, 'Estimated cloud base', true) ?? row('Estimated cloud base', 'No low cloud'));
  if (w.visibilityM !== null) {
    rows.push(row('Visibility', visibilityText(w.visibilityM), visibilityLevel(w.visibilityM / 1000)));
  }
  rows.push(
    row('Freezing level', w.freezingLevelM !== null ? `${fmt(feet100(w.freezingLevelM))} ft` : 'not given'),
  );
  if (w.cape !== null) rows.push(row('Storm risk', capeLabel(w.cape), capeLevel(w.cape)));
  const text = weatherText(w.weatherCode);
  const detail = [
    text,
    w.tempC !== null ? `${fmt(w.tempC, 1)} °C` : null,
    w.humidity !== null ? `humidity ${fmt(w.humidity)} %` : null,
    w.pressureHpa !== null ? `${fmt(w.pressureHpa, 1)} hPa` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return { cloud: w.cloud !== null ? fmt(w.cloud) : '--', rows, detail };
}

export interface RainPanel {
  readonly rate: string;
  readonly word: RainClass;
  readonly level: WeatherLevel;
  readonly lines: readonly string[];
}

/** The precipitation panel (am:31297–31338) with the rate per hour (§15.4). */
export function rainPanel(w: PointWeather): RainPanel | null {
  const rate = precipRateMmH(w);
  if (rate === null) return null;
  const c = rainClass(rate);
  const minutes = w.intervalSeconds ? Math.round(w.intervalSeconds / 60) : 60;
  const text = weatherText(w.weatherCode);
  const lines = [
    text,
    rate > 0 ? `${fmt(w.precipitationMm!, 1)} mm in the last ${minutes} minutes` : 'No precipitation',
    [
      w.humidity !== null ? `Humidity ${fmt(w.humidity)} %` : null,
      w.visibilityM !== null ? `visibility ${visibilityText(w.visibilityM)}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  ].filter((x): x is string => Boolean(x));
  return { rate: fmt(rate, 1), word: c.word, level: c.level, lines };
}

/** Panels read the map centre again 10 s after the map stops and every 5 minutes (am:30634–30641). */
export const PANEL_MOVE_DELAY_MS = 10_000;
export const PANEL_REFRESH_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------------------------- shared state

export interface WeatherPrefs {
  /** Mode bar buttons on, in no particular order. */
  readonly modes: readonly WeatherModeId[];
  /** Panels open. */
  readonly widgets: readonly WeatherWidgetId[];
  /** Panels the mode bar opened (closed again with it, am:29233–29253). */
  readonly auto_widgets: readonly WeatherWidgetId[];
  /** Opacity of each colour-mapped layer, 0.1 to 1 (kept, unlike the Diurnal tool). */
  readonly opacity: Readonly<Partial<Record<WeatherCanvasId, number>>>;
  readonly radar_coverage: boolean;
  /** 0.1 to 0.8 (am:31231). */
  readonly radar_coverage_opacity: number;
}

export const RADAR_COVERAGE_DEFAULT = 0.25;
export const RADAR_COVERAGE_MIN = 0.1;
export const RADAR_COVERAGE_MAX = 0.8;

export const DEFAULT_WEATHER_PREFS: WeatherPrefs = {
  modes: [],
  widgets: [],
  auto_widgets: [],
  opacity: {},
  radar_coverage: true,
  radar_coverage_opacity: RADAR_COVERAGE_DEFAULT,
};

const MODE_IDS = new Set<string>(WEATHER_MODES.map((m) => m.id));
const WIDGET_IDS = new Set<string>(WEATHER_WIDGETS.map(([id]) => id));
const CANVAS_IDS = new Set<string>(Object.keys(CANVAS_FIELD));

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

function idList<T extends string>(v: unknown, ok: ReadonlySet<string>): T[] {
  return Array.isArray(v) ? ([...new Set(v.filter((x) => typeof x === 'string' && ok.has(x)))] as T[]) : [];
}

/** Stored weather preferences made safe; anything unreadable falls back to the defaults. */
export function normaliseWeatherPrefs(raw: unknown): WeatherPrefs {
  if (!isObj(raw)) return DEFAULT_WEATHER_PREFS;
  const opacity: Partial<Record<WeatherCanvasId, number>> = {};
  if (isObj(raw.opacity)) {
    for (const [k, v] of Object.entries(raw.opacity)) {
      const n = num(v);
      if (CANVAS_IDS.has(k) && n !== null) opacity[k as WeatherCanvasId] = clamp(n, 0.1, 1);
    }
  }
  const widgets = idList<WeatherWidgetId>(raw.widgets, WIDGET_IDS);
  const cov = num(raw.radar_coverage_opacity);
  return {
    modes: idList<WeatherModeId>(raw.modes, MODE_IDS),
    widgets,
    auto_widgets: idList<WeatherWidgetId>(raw.auto_widgets, WIDGET_IDS).filter((w) => widgets.includes(w)),
    opacity,
    radar_coverage: raw.radar_coverage !== false,
    radar_coverage_opacity:
      cov === null ? RADAR_COVERAGE_DEFAULT : clamp(cov, RADAR_COVERAGE_MIN, RADAR_COVERAGE_MAX),
  };
}

const without = <T>(l: readonly T[], x: T): T[] => l.filter((y) => y !== x);
const withItem = <T>(l: readonly T[], x: T): T[] => (l.includes(x) ? [...l] : [...l, x]);

/**
 * A mode bar button pressed (am:29266–29271, 29236–29253): the button's layer on or off, its OpenWeatherMap overlay
 * with it, and its panel opened (and remembered as opened by the bar) or closed again when the bar opened it and no
 * other button on still needs it.
 */
export function toggleWeatherMode(
  weather: WeatherPrefs,
  overlays: readonly OverlayId[],
  mode: WeatherModeId,
): { weather: WeatherPrefs; overlays: OverlayId[] } {
  const def = WEATHER_MODES.find((m) => m.id === mode)!;
  const on = !weather.modes.includes(mode);
  const modes = on ? withItem(weather.modes, mode) : without(weather.modes, mode);
  let widgets = [...weather.widgets];
  let auto = [...weather.auto_widgets];
  if (on) {
    if (!widgets.includes(def.widget)) {
      widgets = [...widgets, def.widget];
      auto = withItem(auto, def.widget);
    }
  } else {
    const stillNeeded = WEATHER_MODES.some((m) => m.widget === def.widget && modes.includes(m.id));
    if (!stillNeeded && auto.includes(def.widget)) {
      widgets = without(widgets, def.widget);
      auto = without(auto, def.widget);
    }
  }
  const nextOverlays = def.overlay
    ? on
      ? withItem(overlays, def.overlay)
      : without(overlays, def.overlay)
    : [...overlays];
  return { weather: { ...weather, modes, widgets, auto_widgets: auto }, overlays: nextOverlays };
}

/** A panel opened or closed by the user: from then on the user, not the mode bar, decides when it closes. */
export function setWeatherWidget(weather: WeatherPrefs, widget: WeatherWidgetId, on: boolean): WeatherPrefs {
  if (widget === 'radar' && !on) {
    // the radar panel belongs to the radar layer (am:31209): closing it switches radar off
    return {
      ...weather,
      modes: without(weather.modes, 'radar'),
      widgets: without(weather.widgets, 'radar'),
      auto_widgets: without(weather.auto_widgets, 'radar'),
    };
  }
  return {
    ...weather,
    widgets: on ? withItem(weather.widgets, widget) : without(weather.widgets, widget),
    auto_widgets: without(weather.auto_widgets, widget),
  };
}

export const weatherOpacity = (weather: WeatherPrefs, id: WeatherCanvasId): number =>
  weather.opacity[id] ?? WEATHER_DEFAULT_OPACITY;

/** The colour-mapped layers to draw, in mode bar order. */
export const activeCanvasLayers = (weather: WeatherPrefs): WeatherCanvasId[] =>
  WEATHER_MODES.filter((m) => m.canvas && weather.modes.includes(m.id)).map((m) => m.canvas!);

/** Legend cards to show: the canvas layers on, and the OpenWeatherMap tiles actually drawn. */
export function activeLegends(weather: WeatherPrefs, drawnOverlays: readonly OverlayId[]): WeatherLegend[] {
  const canvas = new Set<LegendId>(activeCanvasLayers(weather));
  if (drawnOverlays.includes('weather_clouds')) canvas.add('cloud');
  if (drawnOverlays.includes('weather_precipitation')) canvas.add('rain');
  return WEATHER_LEGENDS.filter((l) => canvas.has(l.id));
}

/** Which sections a map click shows (am:29395–29418); none: the click is the map's own. */
export function weatherPopupShows(weather: WeatherPrefs, overlays: readonly OverlayId[]): PopupShows {
  const m = (id: WeatherModeId): boolean => weather.modes.includes(id);
  return {
    wind: m('wind') || m('gusts') || weather.widgets.includes('wind'),
    rain: m('rain') || overlays.includes('weather_precipitation'),
    temperature: m('temperature'),
    cloud: m('cloud') || overlays.includes('weather_clouds'),
  };
}

export const anyPopup = (s: PopupShows): boolean => s.wind || s.rain || s.temperature || s.cloud;

// ---------------------------------------------------------------------------------------------- radar

export interface RadarFrame {
  /** Seconds since 1970 (UTC). */
  readonly time: number;
  readonly path: string;
}

export interface RadarFrames {
  readonly host: string;
  readonly frames: readonly RadarFrame[];
}

/** Radar as the Diurnal tool draws it (am:31140–31144, 31173–31181, 31205, 31280). */
export const RADAR = {
  opacity: 0.65,
  maxNativeZoom: 7,
  frameMs: 1200,
  refreshMs: 10 * 60 * 1000,
  /** Above this zoom the panel says to zoom out. */
  zoomHintAbove: 9,
  /** RainViewer allows 100 requests a minute per IP; Teslyn pauses playing at 90. */
  requestsPerMinute: 90,
} as const;

const RAINVIEWER_HOST = /^https:\/\/([a-z0-9-]+\.)*rainviewer\.com$/;
const RADAR_PATH = /^\/v2\/radar\/[A-Za-z0-9]{1,64}$/;

/** The frame list (am:31123–31150): past frames oldest first; the host only when it is RainViewer's own. */
export function parseRainViewerFrames(raw: unknown): Read<RadarFrames> {
  if (!isObj(raw) || !isObj(raw.radar) || !Array.isArray(raw.radar.past)) {
    return { ok: false, error: 'RainViewer sent an answer Teslyn cannot read.' };
  }
  const host =
    typeof raw.host === 'string' && RAINVIEWER_HOST.test(raw.host) ? raw.host : RAINVIEWER_TILE_HOST;
  const frames = raw.radar.past
    .filter(
      (f): f is { time: number; path: string } =>
        isObj(f) && num(f.time) !== null && typeof f.path === 'string' && RADAR_PATH.test(f.path),
    )
    .map((f) => ({ time: f.time, path: f.path }))
    .sort((a, b) => a.time - b.time);
  if (frames.length === 0) return { ok: false, error: 'RainViewer has no radar frames at the moment.' };
  return { ok: true, value: { host, frames } };
}

/** A frame's tiles: Universal Blue (scheme 2), smoothed, snow shown (am:31141). */
export const radarTileUrl = (host: string, frame: RadarFrame): string =>
  `${host}${frame.path}/256/{z}/{x}/{y}/2/1_1.png`;

/** "14:50 UTC, 4 min ago", or "now" under 2 minutes (am:31163–31167). */
export function radarFrameText(frame: RadarFrame, now: number): string {
  const d = new Date(frame.time * 1000);
  const hhmm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`;
  const mins = Math.round((now - frame.time * 1000) / 60_000);
  return `${hhmm}, ${mins < 2 ? 'now' : `${mins} min ago`}`;
}

/** After a refresh: the newest frame if none was shown or the newest was; else the same frame, or the nearest. */
export function radarIndexAfterRefresh(
  old: readonly RadarFrame[],
  oldIndex: number,
  next: readonly RadarFrame[],
): number {
  if (next.length === 0) return -1;
  if (oldIndex < 0 || oldIndex >= old.length - 1) return next.length - 1;
  const shown = old[oldIndex]!;
  const same = next.findIndex((f) => f.path === shown.path);
  if (same >= 0) return same;
  const later = next.findIndex((f) => f.time > shown.time);
  return later >= 0 ? later : next.length - 1;
}

export const nextFrame = (i: number, n: number): number => (n === 0 ? -1 : i >= n - 1 ? 0 : i + 1);

export const RADAR_PAUSED_TEXT =
  'Radar paused: RainViewer allows 100 tile requests a minute from this computer. Press Play again in a minute.';

/** Requests in a rolling window (RainViewer's per-minute limit). */
export class RequestWindow {
  private times: number[] = [];
  constructor(
    private readonly limit: number = RADAR.requestsPerMinute,
    private readonly windowMs: number = 60_000,
  ) {}

  record(now: number): void {
    this.trim(now);
    this.times.push(now);
  }

  count(now: number): number {
    this.trim(now);
    return this.times.length;
  }

  full(now: number): boolean {
    return this.count(now) >= this.limit;
  }

  private trim(now: number): void {
    const from = now - this.windowMs;
    while (this.times.length > 0 && this.times[0]! <= from) this.times.shift();
  }
}
